import React, { useEffect, useRef, useState } from "react";

/**
 * Parallax wrapper — moves children at a different scroll speed.
 * @param {number} speed - positive = moves slower / up; negative = opposite (0.15–0.4 typical)
 * @param {boolean} scale - subtle scale on scroll
 */
export function Parallax({
  children,
  speed = 0.25,
  scale = false,
  className = "",
  as: Tag = "div",
  style = {},
}) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const [scaleVal, setScaleVal] = useState(1);
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion.current) return;

    let ticking = false;

    const update = () => {
      const node = ref.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const viewH = window.innerHeight || 1;
      // Progress of element through viewport (-1 → 1-ish)
      const progress = (viewH / 2 - (rect.top + rect.height / 2)) / viewH;
      setOffset(progress * speed * 120);
      if (scale) {
        setScaleVal(1 + Math.min(Math.abs(progress) * 0.06, 0.08));
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [speed, scale]);

  return (
    <Tag
      ref={ref}
      className={`parallax-wrap ${className}`}
      style={{
        ...style,
        transform: reducedMotion.current
          ? undefined
          : `translate3d(0, ${offset}px, 0) scale(${scaleVal})`,
        willChange: "transform",
      }}
    >
      {children}
    </Tag>
  );
}

/**
 * Soft floating background shapes for depth (parallax layers).
 */
export function ParallaxBg({ className = "" }) {
  return (
    <div className={`parallax-bg ${className}`} aria-hidden="true">
      <span className="parallax-orb parallax-orb-a" />
      <span className="parallax-orb parallax-orb-b" />
      <span className="parallax-orb parallax-orb-c" />
    </div>
  );
}

export default Parallax;
